import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

interface WhatsAppWidgetProps {
  phoneNumber: string;
  welcomeMessage: string;
}

const WhatsAppWidget: React.FC<WhatsAppWidgetProps> = ({ 
  phoneNumber = "918295531760",
  welcomeMessage = "Hello! I'm interested in learning more about your services."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if user is on a mobile device
    const checkMobile = () => {
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const handleWhatsAppClick = () => {
    // Format phone number - ensure no spaces or special characters
    const formattedNumber = phoneNumber.replace(/\D/g, '');
    
    // Encode message properly
    const encodedMessage = encodeURIComponent(welcomeMessage);
    
    // Different URLs for mobile and desktop
    // For mobile, use the whatsapp:// protocol to open the app
    const url = isMobile
      ? `whatsapp://send?phone=${formattedNumber}&text=${encodedMessage}`
      : `https://web.whatsapp.com/send?phone=${formattedNumber}&text=${encodedMessage}`;
    
    // Open in new tab
    window.open(url, '_blank');
    
    // Close the widget popup
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors duration-300 focus:outline-none"
        aria-label={isOpen ? "Close WhatsApp chat" : "Open WhatsApp chat"}
      >
        <FontAwesomeIcon icon={isOpen ? faTimes : faWhatsapp} size="lg" />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-20 right-0 w-72 bg-white rounded-lg shadow-xl overflow-hidden"
          >
            <div className="bg-green-500 p-4 text-white">
              <h3 className="font-bold text-lg">Chat with Lisha</h3>
              <p className="text-sm text-green-100">We typically reply within minutes</p>
            </div>
            
            <div className="p-4">
              <p className="text-gray-700 mb-4">Hello! How can we help you today?</p>
              <button
                onClick={handleWhatsAppClick}
                className="w-full py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center justify-center transition-colors duration-300"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
                Start Chat
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhatsAppWidget; 